import { getUrlParam } from '@blk/aw-api-clients';
import { AdobeConfig } from '@blk/aw-bff-typescript-models';
import { search } from '@jmespath-community/jmespath';
import { JSONValue } from '@jmespath-community/jmespath/dist/types/JSON.type';
import { get, isEmpty } from 'lodash';
import {
  ADOBE_DOMAIN_SPECIFIC_INFO_KEY,
  ADOBE_SITE_NAME,
  ADOBE_SITE_SECTION,
  BULK_SECURITY_ANALYZER_PATH,
  COMPARISON_IDS_KEY_FOR_ADOBE,
  COMPARISON_JOURNEY_ACTIVE_KEY,
  COMPARISON_PATH,
  COMPONENT_INDICATORS,
  CustomLogAttribute,
  DEFAULT_OT_SCRIPT_ID,
  DEFAULT_REPORT_SUITE_ID,
  DEFAULT_TEALIUM_PROFILE,
  ENV_DEV,
  ENV_PROD,
  LogAction,
  MAX_LINK_NAME_LENGTH,
  MODEL_EVALUATOR_PATH,
  NEW_ANALYSIS_PATH,
  PORTFOLIO_SELECTION_PATH,
  SUB_FIELD_INDICATORS,
  TOOL_STATE_COMPLETE,
  UNKNOWN,
} from '../constants';
import { Logger } from '../Logger';
import {
  AdobeAnalyticsInfo,
  AdobeUserActions,
  AppMetadata,
  DEFAULT_ACTION_METADATA_CONFIG,
  DomainSpecificInfoType,
  EVENT_TYPE_TO_USER_ACTION,
  EventType,
  GlobalAnalyticsParams,
  LinkEvent,
  SITE_SUB_SECTION_TO_TOOL_NAME,
  TOOL_NAME,
  TRACKED_ACTION_TO_USER_ACTION,
  TrackingData,
  USER_ACTION_TO_SEARCH_TERMS,
} from '../models';
import { ElementNode, LoggerUtil } from './LoggerUtils';

/**
 * This function loads jQuery asynchronously
 */

const loadJQuery = async () => {
  try {
    const module = await import('jquery/dist/jquery.slim');
    window.$ = module.default;
  } catch (error) {
    console.error('Failed to load jquery', error);
  }
};
/**
 * Checks if the current environment is a development environment
 * @returns boolean indicating if the current environment is dev
 */
export const isDevEnv = () =>
  ['dev.blackrock.com', 'localhost', 'tst.blackrock.com'].includes(window.location.hostname);

/**
 * This function gets the environment for Adobe Analytics
 * @param appMetadata app metadata for determining environment from config
 * @returns string environment value
 */
export const getEnv = (appMetadata: AppMetadata) => appMetadata.adobeConfig?.env || (isDevEnv() ? ENV_DEV : ENV_PROD);

/**
 * This function includes the tealium script
 */
const includeTealiumScript = (appMetadata: AppMetadata): void => {
  const env = getEnv(appMetadata);
  const profile = appMetadata.adobeConfig?.tealiumProfile || DEFAULT_TEALIUM_PROFILE;
  const js = document.createElement('script');
  js.type = 'text/javascript';
  js.src = `https://tags.tiqcdn.com/utag/blackrock/${profile}/${env}/utag.js`;
  js.async = true;
  js.onerror = (e) => {
    console.error('Failed to load tealium script', e);
  };
  document.head.appendChild(js);
};

/*
 * This function sets up the OneTrust Cookie Manager
 */
const setupOneTrustCM = (appMetadata: AppMetadata) => {
  // auto block script
  const baseUrl = 'https://cdn.cookielaw.org';
  const scriptId = appMetadata.adobeConfig?.otScriptId ?? DEFAULT_OT_SCRIPT_ID;
  const autoBlockJs = document.createElement('script');
  autoBlockJs.type = 'text/javascript';
  autoBlockJs.src = `${baseUrl}/consent/${scriptId}/OtAutoBlock.js`;
  autoBlockJs.onerror = (e) => {
    console.error('Failed to load ot auto block script', e);
  };
  // otSDK script
  const js = document.createElement('script');
  js.type = 'text/javascript';
  js.src = `${baseUrl}/scripttemplates/otSDKStub.js`;
  js.setAttribute('data-document-language', 'true');
  js.setAttribute('data-domain-script', scriptId);
  js.async = true;
  js.onerror = (e) => {
    console.error('Failed to load OT sdk script', e);
  };
  document.head.appendChild(js);
  document.head.appendChild(autoBlockJs);
};

/**
 * Get the tool name based on the current route and site sub section
 * @param currentRoute - The current route
 * @param siteSubSectionToToolName - The site sub section to tool name mapping
 * @param siteSubSection - The site sub section
 * @returns The tool name
 */
const getToolName = (
  currentRoute: string,
  siteSubSectionToToolName: Record<string, string>,
  siteSubSection: string
): string => {
  if (!!getUrlParam(COMPARISON_IDS_KEY_FOR_ADOBE) || currentRoute.includes(PORTFOLIO_SELECTION_PATH)) {
    if (currentRoute.includes(BULK_SECURITY_ANALYZER_PATH)) {
      return TOOL_NAME.BULK_SECURITY_ANALYZER;
    } else if (currentRoute.includes(COMPARISON_PATH)) {
      return TOOL_NAME.PORTFOLIO_ANALYZER;
    }
  }
  return siteSubSectionToToolName[siteSubSection];
};

/**
 * This function sets the global analytics parameters
 */
export const setAnalyticsParams = (appMetadata: AppMetadata) => {
  const env = getEnv(appMetadata);
  const lang = appMetadata.languagePreferenceFn?.() || 'en';
  const isInternalUser = appMetadata.internalUserFn?.();
  const localeParts = Intl.DateTimeFormat().resolvedOptions().locale.split('-');
  const countryCode = localeParts[1] ? localeParts[1].toLowerCase() : 'us';
  const currentRoute = LoggerUtil.getHash(window.location.href, appMetadata) || '';
  const siteSubSection = LoggerUtil.getFeatureNameFromUrl(appMetadata);
  // Derive view name by replacing hyphens with spaces
  const viewName = siteSubSection.replaceAll('-', ' ');
  const siteSubSectionToToolName = {
    ...SITE_SUB_SECTION_TO_TOOL_NAME,
    ...appMetadata.adobeConfig?.siteSubSectionToToolNameMappings,
  };
  // Set toolName to Portfolio Analyzer when user lands on comparison page with valid comparisonIds or when portfolio analysis mode is enabled
  const toolName = getToolName(currentRoute, siteSubSectionToToolName, siteSubSection);

  window.adobe_report_suite_id = appMetadata.adobeConfig?.reportSuiteId || DEFAULT_REPORT_SUITE_ID;
  window.globalAnalyticsParams = {
    ...window.globalAnalyticsParams,
    /*
     * enable analytics only when marketing/tracking/profiling Cookies are allowed
     * and can be determined by the presence of the C0004 cookie
     */
    analyticsAllowed: window.OptanonActiveGroups?.includes('C0004') ? 'true' : 'false',
    siteName: ADOBE_SITE_NAME,
    siteSection: ADOBE_SITE_SECTION,
    siteSubSection, // e.g. my-saved-portfolios
    toolName,
    pageName: currentRoute, // e.g. my-saved-portfolios/portfolio-details/port-id
    padlockedContent: 'yes',
    loggedIn: 'yes',
    envType: env,
    country: countryCode,
    languageSelector: lang,
    // fallback to userId if webTrackingId is not available
    webTrackingId: appMetadata.webTrackingId ?? appMetadata.userId,
    ...(isInternalUser ? { isInternalTraffic: 'true' } : {}),
    viewName,
    enableAbTesting: appMetadata.adobeConfig?.enableAbTesting ? 'true' : 'false',
    atmosId: appMetadata.atmosUserId !== appMetadata.userId ? appMetadata.atmosUserId : undefined,
    webEntityId: appMetadata.webEntityId,
    userFirm: appMetadata.userFirm,
  } as GlobalAnalyticsParams;
};

/**
 * This function sets a single analytics parameter by key
 * @param key keyof GlobalAnalyticsParams
 * @param value string, value to set
 */
export const setAnalyticsParamByKey = (key: keyof GlobalAnalyticsParams, value: string) => {
  if (!window.globalAnalyticsParams) {
    window.globalAnalyticsParams = {} as GlobalAnalyticsParams;
  }
  window.globalAnalyticsParams[key] = value;
};

/**
 * This function sets up the consent event listener
 */
const setupConsentEventListener = (appMetadata: AppMetadata) => {
  window.addEventListener('consent.onetrust', (_e) => {
    setAnalyticsParams(appMetadata);
  });
};

/**
 * Handles tool complete tracking logic
 * @param newHash Current hash from URL
 * @param oldHash Previous hash from analytics params
 * @param comparisonIds Comparison IDs from URL params
 */
const handleToolComplete = (newHash: string, oldHash: string | undefined, comparisonIds: string): void => {
  const isModelEvaluatorLandingPage = newHash === MODEL_EVALUATOR_PATH;

  // Clear journey state when leaving comparison or reaching new-analysis
  const shouldClearJourney =
    !(newHash.includes(COMPARISON_PATH) || newHash.includes(BULK_SECURITY_ANALYZER_PATH)) ||
    newHash.includes(NEW_ANALYSIS_PATH) ||
    !comparisonIds;

  if (shouldClearJourney) {
    sessionStorage.removeItem(COMPARISON_JOURNEY_ACTIVE_KEY);
    setAnalyticsParamByKey('toolState', '');
    // Model evaluator landing page is a exception and will be checked for a tool complete
    if (!isModelEvaluatorLandingPage) {
      return;
    }
  }

  const journeyActive = sessionStorage.getItem(COMPARISON_JOURNEY_ACTIVE_KEY) === 'true';
  /*
   * Send additional event to mark tool complete
   * TODO: Remove this once we are able to track tool completion via adobe fully
   */
  const domainSpecificInfo: DomainSpecificInfoType = JSON.parse(
    sessionStorage.getItem(ADOBE_DOMAIN_SPECIFIC_INFO_KEY) || '{}'
  );

  /*
   * Set toolState complete when user lands on comparison page/model evaluator except new-analysis from a valid previous page
   * Comparison pages require comparisonIds and use journey tracking, model-evaluator does not
   */
  const isToolStateComplete =
    (!journeyActive &&
      (newHash.includes(COMPARISON_PATH) || newHash.includes(BULK_SECURITY_ANALYZER_PATH)) &&
      !!comparisonIds &&
      !isEmpty(domainSpecificInfo)) ||
    (isModelEvaluatorLandingPage && !newHash.includes(NEW_ANALYSIS_PATH) && !!oldHash);

  // Only set toolState when marking as complete, don't clear it during active journey
  if (isToolStateComplete) {
    setAnalyticsParamByKey('toolState', TOOL_STATE_COMPLETE);
    /*
     * Set toolName to Portfolio Analyzer when user completes analysis
     * Set journey flag only for comparison pages, not for model-evaluator
     */
    if (!isModelEvaluatorLandingPage) {
      setAnalyticsParamByKey(
        'toolName',
        newHash.includes(COMPARISON_PATH) ? TOOL_NAME.PORTFOLIO_ANALYZER : TOOL_NAME.BULK_SECURITY_ANALYZER
      );
      sessionStorage.setItem(COMPARISON_JOURNEY_ACTIVE_KEY, 'true');
    }
    domainSpecificInfo.additionalAttributes = {
      ...domainSpecificInfo.additionalAttributes,
      toolName: window.globalAnalyticsParams?.toolName || '',
      pageName: window.globalAnalyticsParams?.pageName || '',
    };
    Logger.trackEvent({
      action: LogAction.TOOL_COMPLETE,
      domainSpecificInfo,
      elementInfo: {
        eventType: EventType.EVENT_LOAD,
      },
    });
  }
};

/**
 * Removes portfolio type from comparison IDs
 * @param comparisonIds - Comma-separated comparison IDs in format "idType:idValue:portfolioType"
 * @returns Comma-separated comparison IDs without portfolio type in format "idType:idValue"
 */
export const removePortfolioTypeFromComparisonIds = (comparisonIds: string) =>
  comparisonIds
    .split(',')
    .map((id) => {
      const parts = id.split(':');
      return parts.slice(0, 2).join(':');
    })
    .join(',');

/**
 * This function handles route changes to send page view events
 * @param appMetadata app metadata for setting analytics params
 * @returns void
 */
const handleRouteChange = (appMetadata: AppMetadata) => {
  const newUrl = window.location.href;
  const oldHash = window.globalAnalyticsParams?.pageName;
  const newHash = LoggerUtil.getHash(newUrl, appMetadata);
  // Return without doing anything if no hash or no change in hash
  if ((oldHash && newHash && oldHash === newHash) || !newHash) {
    return;
  }
  // set pageName param if hash changed
  setAnalyticsParamByKey('pageName', newHash);
  // Retrieve comparison ids from URL and set it for adobe
  const comparisonIds = getUrlParam(COMPARISON_IDS_KEY_FOR_ADOBE);
  setAnalyticsParamByKey('portfolioIds', comparisonIds);
  setAnalyticsParams(appMetadata);

  // Handle tool complete tracking - defer to allow React components to mount and set sessionStorage
  setTimeout(() => {
    handleToolComplete(newHash, oldHash, comparisonIds);
    if (typeof window.utag?.view === 'function') {
      // Send page view event
      window.utag.view({
        page_name: newHash,
        page_url: newUrl,
      });
    }
  }, 0);
};

/**
 * This function sets up the route change listener, to send the page view events
 */
const setupRouteChangeListener = (appMetadata: AppMetadata) => {
  window.addEventListener('popstate', () => {
    // Use setTimeout to ensure that the page view and params are set after the link event is sent
    setTimeout(() => {
      handleRouteChange(appMetadata);
    }, 0);
  });
};

/**
 * This function loads the adobe analytics script
 */
export const loadAdobeAnalyticsScript = async (appMetadata: AppMetadata) => {
  setupOneTrustCM(appMetadata);
  await loadJQuery();
  setAnalyticsParams(appMetadata);
  includeTealiumScript(appMetadata);
  setupConsentEventListener(appMetadata);
  setupRouteChangeListener(appMetadata);
};

/**
 * Track custom link/click events
 */
export const utagLink = (options: LinkEvent) => {
  if (typeof window.utag?.link === 'function') {
    window.utag.link(options);
  }
};

/**
 * Extract Adobe Analytics specific information from DOM element
 */
const extractAdobeAnalyticsFromElement = (
  element: ElementNode | null | undefined,
  reactFiber: ElementNode | undefined
): AdobeAnalyticsInfo => {
  if (!element) {
    return {};
  }

  return {
    componentName:
      LoggerUtil.getElementAttributeFromTree(element, reactFiber, CustomLogAttribute.ADOBE_COMPONENT_NAME) || undefined,
    subFieldName:
      LoggerUtil.getElementAttributeFromTree(element, reactFiber, CustomLogAttribute.ADOBE_SUB_FIELD_NAME) || undefined,
    userAction:
      (LoggerUtil.getElementAttributeFromTree(
        element,
        reactFiber,
        CustomLogAttribute.ADOBE_USER_ACTION
      ) as AdobeUserActions) || undefined,
    label: LoggerUtil.getElementAttributeFromTree(element, reactFiber, CustomLogAttribute.ADOBE_LABEL) || undefined,
  };
};

/**
 * Extract component name from DOM element attributes or class names
 */
const extractComponentFromDOM = (
  element: ElementNode | null | undefined,
  reactFiber: ElementNode | undefined
): string => {
  if (!element) {
    return '';
  }

  // Check common component indicators in data test id
  const dataTestId = LoggerUtil.getElementAttributeFromTree(element, reactFiber, 'data-testid') || '';
  for (const indicator of COMPONENT_INDICATORS) {
    if (dataTestId.includes(indicator)) {
      return indicator;
    }
  }
  // Fallback to data test id
  return dataTestId;
};

/**
 * Extract sub-field name from DOM element
 */
const extractSubFieldFromDOM = (
  element: ElementNode | null | undefined,
  reactFiber: ElementNode | undefined
): string => {
  if (!element) {
    return '';
  }

  // Check common component sub field indicators in data test id
  const dataTestId = LoggerUtil.getElementAttributeFromTree(element, reactFiber, 'data-testid') || '';
  for (const indicator of SUB_FIELD_INDICATORS) {
    if (dataTestId.includes(indicator)) {
      return indicator;
    }
  }

  return '';
};

/**
 * Extract action from event or element
 */
const extractActionFromEvent = (trackingData: TrackingData, label: string): string | undefined => {
  const action = trackingData.action?.toLowerCase() ?? '';
  // Iterate over all AdobeUserActions values and match if action includes the value
  for (const userAction of Object.values(AdobeUserActions)) {
    const searchTerms = [userAction, ...(USER_ACTION_TO_SEARCH_TERMS[userAction] ?? [])];
    if (searchTerms.some((term) => action.includes(term) || label.toLowerCase().includes(term))) {
      return userAction;
    }
  }
  return undefined;
};

/**
 * Clean and sanitize string values
 */
const cleanString = (value: string): string =>
  LoggerUtil.sanitize(value)
    .trim()
    .replace(/[|]/g, '-') // Replace pipe characters to avoid conflicts
    .replace(/\s+/g, ' '); // Normalize whitespace

/**
 * Extract action-specific metadata from tracking data using config mapping
 * Supports both direct values and stringified JSON with nested path extraction
 */
const extractActionMetadata = (
  trackingData: TrackingData,
  adobeConfig: AdobeConfig | undefined
): string | undefined => {
  const mergedConfig = {
    ...DEFAULT_ACTION_METADATA_CONFIG,
    ...adobeConfig?.logActionMetadataConfigs,
  };
  const metadataConfig = mergedConfig[trackingData.action];
  if (!metadataConfig) {
    return undefined;
  }
  const parts: string[] = [];
  metadataConfig.forEach((config) => {
    const rawValue = get(trackingData, config.path);

    if (config.jsonPath && typeof rawValue === 'string') {
      // Value is stringified JSON, parse and extract nested path
      try {
        const parsed: JSONValue = JSON.parse(rawValue);
        const nestedValue = search(parsed, config.jsonPath);
        if (nestedValue) {
          // eslint-disable-next-line @typescript-eslint/no-base-to-string -- will get a proper values as we are extracting the known values
          parts.push(`${config.jsonPath}/${nestedValue.toString()}`);
        }
      } catch {
        // JSON parse failed, skip metadata
      }
    } else if (rawValue) {
      // If value is a string or number or boolean, return it as a string
      parts.push(`${config.path}/${String(rawValue)}`);
    }
  });

  return parts.join(',');
};

/**
 * Compute link name from tracking data using new Adobe Analytics format
 * @param trackingData Tracking data retrieved from the event
 * @returns Link name: {component/field name*} | {sub-field name} | {custom event name} | {user-action*} | {label*} | {metadata}
 */
export const getLinkName = (trackingData: TrackingData, appMetadata: AppMetadata): string => {
  const customEventName = trackingData.action !== LogAction.ELEMENT_CLICKED ? cleanString(trackingData.action) : null;
  // If custom event name is unavailable and skip default events is true, then skip adobe tracking
  if (!customEventName && appMetadata?.adobeConfig?.skipDefaultEvents) {
    return '';
  }
  const element = trackingData.srcElement as ElementNode | null | undefined;
  const reactFiber = element ? LoggerUtil.getReactFiber(element) : undefined;
  const adobeLinkName = element
    ? LoggerUtil.getElementAttributeFromTree(element, reactFiber, CustomLogAttribute.ADOBE_LINK_NAME)
    : undefined;
  // if adobe link name is explicitly set, use it
  if (adobeLinkName && !isEmpty(adobeLinkName)) {
    return LoggerUtil.sanitize(adobeLinkName);
  }
  // Try to get Adobe-specific data first from tracking data or DOM
  const adobeInfo = trackingData.adobeAnalytics || extractAdobeAnalyticsFromElement(element, reactFiber);

  // Get component/field name (Mandatory)
  let componentName =
    adobeInfo?.componentName ||
    trackingData.componentName ||
    extractComponentFromDOM(element, reactFiber) ||
    trackingData.featureInfo?.sectionName ||
    trackingData.featureInfo?.featureName ||
    LoggerUtil.getFeatureNameFromUrl(appMetadata) ||
    UNKNOWN;

  // Add section name to component name if it's a modal
  if (
    trackingData.featureInfo?.sectionName?.includes('modal') &&
    componentName !== trackingData.featureInfo?.sectionName
  ) {
    componentName = `${componentName ? componentName + '/' : ''}${trackingData.featureInfo?.sectionName}`;
  }

  // Get sub-field name (Optional)
  const subFieldName =
    adobeInfo?.subFieldName ||
    trackingData.elementInfo?.elementMetadata ||
    extractSubFieldFromDOM(element, reactFiber) ||
    '';

  // Extract action-specific metadata
  const actionMetadataStr = extractActionMetadata(trackingData, appMetadata.adobeConfig);

  // Get label (Mandatory)
  let label =
    adobeInfo.label ||
    LoggerUtil.getElementNameLocal(trackingData, element ?? null) ||
    trackingData.elementInfo?.elementName ||
    actionMetadataStr ||
    UNKNOWN;

  // Get user action (Mandatory)
  let userAction =
    adobeInfo?.userAction ||
    TRACKED_ACTION_TO_USER_ACTION[trackingData.action] ||
    extractActionFromEvent(trackingData, label) ||
    EVENT_TYPE_TO_USER_ACTION[trackingData.elementInfo?.eventType ?? 1] ||
    AdobeUserActions.LINK_CLICK;

  // Clean and format the values
  componentName = cleanString(componentName);
  userAction = cleanString(userAction);

  // Construct the link name in the required format
  const parts = [
    componentName,
    subFieldName ? cleanString(subFieldName) : null,
    customEventName ? cleanString(customEventName) : null,
    userAction,
    cleanString(label),
    actionMetadataStr && label !== actionMetadataStr ? cleanString(actionMetadataStr) : null,
  ].filter(Boolean);
  let linkName = parts.join(' | ');
  if (linkName.length > MAX_LINK_NAME_LENGTH) {
    // Truncate to fit within the limit
    linkName = linkName.slice(0, MAX_LINK_NAME_LENGTH - 3) + '...';
  }
  return linkName.toLowerCase();
};
